import React, { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase/firebase";

const DOCUMENTS = [
  { label: "Passport", fileKeys: ["passportFile", "passportFileBack"], expiryKey: "passportExpiry" },
  { label: "Emirates ID", fileKeys: ["emiratesIdFile", "emiratesIdFileBack"], expiryKey: "emiratesIdExpiry" },
  { label: "Driving License", fileKeys: ["licenseFile", "licenseFileBack"], expiryKey: "licenseExpiry" },
  { label: "RTA Permit", fileKeys: ["rtaPermitFile"], expiryKey: "rtaPermitExpiry" },
  { label: "RTA Training", fileKeys: ["rtaTrainingFile"], expiryKey: "rtaTrainingExpiry" },
  { label: "Health Insurance", fileKeys: ["healthInsuranceFile"], expiryKey: "healthInsuranceExpiry" },
];

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value?.seconds) return new Date(value.seconds * 1000);
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.slice(0, 10))) {
    return new Date(`${value.slice(0, 10)}T12:00:00`);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function driverDocumentSummary(driver) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const in30 = new Date(today);
  in30.setDate(in30.getDate() + 30);

  let missing = 0;
  let expired = 0;
  let expiring = 0;
  let complete = 0;

  DOCUMENTS.forEach((docDef) => {
    const filesOk = docDef.fileKeys.every((key) => Boolean(driver?.[key]));
    if (!filesOk) missing += 1;
    else complete += 1;

    const expiry = toDate(driver?.[docDef.expiryKey]);
    if (!expiry) return;
    expiry.setHours(0, 0, 0, 0);
    if (expiry < today) expired += 1;
    else if (expiry <= in30) expiring += 1;
  });

  return { missing, expired, expiring, complete };
}

function isCancelled(driver) {
  return ["cancelled", "inactive", "terminated"].includes(
    String(driver?.status || "").trim().toLowerCase()
  );
}

export default function DriverDocumentsList() {
  const navigate = useNavigate();
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const allowed = ["docs_link", "admin", "md"].includes(role);

  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [company, setCompany] = useState("All");
  const [includeCancelled, setIncludeCancelled] = useState(false);

  useEffect(() => {
    if (!allowed) return undefined;
    const unsub = onSnapshot(
      collection(db, "drivers"),
      (snap) => {
        setDrivers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Driver document list failed:", err);
        setError("Unable to load employees. Please check the published Firestore rules.");
        setLoading(false);
      }
    );
    return () => unsub();
  }, [allowed]);

  const companies = useMemo(
    () => Array.from(new Set(drivers.map((d) => d.company).filter(Boolean))).sort(),
    [drivers]
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return drivers
      .filter((d) => (includeCancelled ? true : !isCancelled(d)))
      .filter((d) => company === "All" || d.company === company)
      .filter((d) => {
        if (!q) return true;
        return [d.fullName, d.phone, d.company, d.assignedCarNumber]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      })
      .sort((a, b) => String(a.fullName || "").localeCompare(String(b.fullName || "")));
  }, [drivers, search, company, includeCancelled]);

  if (!allowed) {
    return <div style={{ padding: 24, color: "#b91c1c", fontWeight: 800 }}>Not authorized.</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 1450, margin: "0 auto" }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0 }}>Driver Documents</h2>
        <div style={{ marginTop: 6, color: "#64748b", fontSize: 13 }}>
          Upload driver documents and expiry dates directly. This screen does not allow changes to status, company, car assignment, salary or other employee details.
        </div>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16, alignItems: "center" }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search driver, phone or car"
          style={inputStyle({ minWidth: 280 })}
        />
        <select value={company} onChange={(e) => setCompany(e.target.value)} style={inputStyle()}>
          <option value="All">All Companies</option>
          {companies.map((name) => <option key={name} value={name}>{name}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 7, fontSize: 13, fontWeight: 700, color: "#475569" }}>
          <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} />
          Include cancelled
        </label>
        <span style={{ marginLeft: "auto", color: "#64748b", fontSize: 13, fontWeight: 700 }}>
          {rows.length} employee{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      {error ? <div style={alertStyle}>{error}</div> : null}

      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 14, overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
          <thead>
            <tr style={{ background: "#f8fafc", color: "#475569", fontSize: 12 }}>
              <th style={th}>Driver</th>
              <th style={th}>Company</th>
              <th style={th}>Current Car</th>
              <th style={th}>Documents</th>
              <th style={th}>Expired</th>
              <th style={th}>Expiring ≤30 Days</th>
              <th style={th}>Missing</th>
              <th style={{ ...th, textAlign: "right" }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={emptyCell}>Loading employees…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={emptyCell}>No employees found.</td></tr>
            ) : rows.map((driver) => {
              const summary = driverDocumentSummary(driver);
              return (
                <tr key={driver.id} style={{ borderTop: "1px solid #eef2f7" }}>
                  <td style={td}>
                    <div style={{ fontWeight: 900, color: "#0f172a" }}>{driver.fullName || "Unnamed employee"}</div>
                    <div style={{ marginTop: 3, fontSize: 12, color: "#64748b" }}>{driver.phone || ""}</div>
                  </td>
                  <td style={td}>{driver.company || "-"}</td>
                  <td style={td}>{driver.assignedCarNumber || "-"}</td>
                  <td style={td}><b>{summary.complete}/{DOCUMENTS.length}</b> complete</td>
                  <td style={{ ...td, color: summary.expired ? "#b91c1c" : "#334155", fontWeight: 800 }}>{summary.expired}</td>
                  <td style={{ ...td, color: summary.expiring ? "#c2410c" : "#334155", fontWeight: 800 }}>{summary.expiring}</td>
                  <td style={{ ...td, color: summary.missing ? "#b91c1c" : "#334155", fontWeight: 800 }}>{summary.missing}</td>
                  <td style={{ ...td, textAlign: "right" }}>
                    <button
                      onClick={() => navigate(`/driver-documents/${driver.id}`)}
                      style={manageButton}
                    >
                      Manage Documents
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function inputStyle(extra = {}) {
  return {
    padding: "9px 11px",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    background: "white",
    ...extra,
  };
}

const th = { textAlign: "left", padding: "11px 12px", fontWeight: 900, whiteSpace: "nowrap" };
const td = { padding: "12px", color: "#334155", verticalAlign: "middle" };
const emptyCell = { padding: 24, textAlign: "center", color: "#64748b" };
const manageButton = { border: 0, borderRadius: 8, padding: "8px 11px", background: "#4f46e5", color: "white", fontWeight: 900, cursor: "pointer" };
const alertStyle = { marginBottom: 14, padding: 12, borderRadius: 9, background: "#fef2f2", color: "#b91c1c", fontWeight: 700 };
